/**
 * @file The archive of the selected report: its runs read once under the
 * bounded archive query, the chip dates, the chosen date and the run that date
 * opens, and the read's error. Consumer: `AnalyticsReportsPanel`, which hands
 * them to the Archive and Preview cards. A full admin read drops its oldest instant when it holds
 * fewer runs than current recipients, so a chip never opens a run set missing
 * a recipient. A chosen date belongs to the report it was chosen on, so
 * selecting another report shows its newest date, and a chosen date that
 * drops out of the dates falls back to the newest, without an effect.
 */
import { useMemo, useState } from 'react';

import type {
  AnalyticsReportArchive,
  AnalyticsReportSelection,
} from './analyticsReportsTab.types';
import { analyticsReportArchiveDates } from './analyticsReportArchiveDates';
import { analyticsReportArchiveQuery } from './analyticsReportArchiveQuery';
import { analyticsReportPreviewRun } from './analyticsReportPreviewRun';
import { useAnalyticsReportRuns } from './useAnalyticsReportRuns';

export const useAnalyticsReportArchive = (
  selection: AnalyticsReportSelection | null,
  viewer: { isAdmin: boolean; uid: string; canReadOrg: boolean },
): AnalyticsReportArchive => {
  const [chosen, setChosen] = useState<{
    reportId: string;
    scheduledAt: string;
  } | null>(null);
  const { isAdmin, uid, canReadOrg } = viewer;
  const recipientCount = selection?.recipientUids.length ?? 0;
  const { queryOptions, adminLimit } = useMemo(
    () =>
      analyticsReportArchiveQuery({ isAdmin, uid, canReadOrg }, recipientCount),
    [isAdmin, uid, canReadOrg, recipientCount],
  );
  const { runs, loading, error } = useAnalyticsReportRuns(
    selection?.reportId ?? null,
    queryOptions,
  );
  const dates = analyticsReportArchiveDates(
    runs.map(({ run }) => run),
    adminLimit,
    recipientCount,
  );
  const date =
    chosen !== null &&
    chosen.reportId === selection?.reportId &&
    dates.includes(chosen.scheduledAt)
      ? chosen.scheduledAt
      : (dates[0] ?? null);

  return {
    dates,
    date,
    loading,
    error,
    preview:
      selection === null || date === null
        ? null
        : analyticsReportPreviewRun({
            runs,
            scheduledAt: date,
            uid,
            recipientUids: selection.recipientUids,
          }),
    choose: (scheduledAt) => {
      if (selection !== null)
        setChosen({ reportId: selection.reportId, scheduledAt });
    },
  };
};
